import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaArrowLeft, FaShoppingCart, FaImage, FaCheck, FaBoxOpen, FaPercentage, FaSpinner
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { API, getImageUrl } from "../config/api";
import { useCart } from "../context/CartContext";
import SEO from "../components/SEO";

function PackDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [error, setError] = useState(null);
  const [added, setAdded] = useState(false);

  const { addToCart } = useCart();

  useEffect(() => {
    const fetchPack = async () => {
      try {
        const res = await axios.get(`${API.PACKS}/${id}`);
        setPack(res.data);
      } catch (err) {
        console.error("Erreur fetch pack :", err);
        setError("Pack promotionnel introuvable ou expiré.");
      } finally {
        setLoading(false);
      }
    };
    fetchPack();
  }, [id]);

  const handleAddToCart = async () => {
    if (!pack) return;
    
    try {
      await axios.post(`${API.PACKS}/${pack._id}/click`);
    } catch (e) {
      // ignore analytics error
    }

    let packImages = [];
    if (pack.images && pack.images.length > 0) {
      packImages = pack.images;
    } else if (pack.bannerImage) {
      packImages = [pack.bannerImage];
    }
    
    const fakeProduct = {
      _id: pack._id,
      name: pack.name,
      type: "Pack",
      images: packImages,
      mainImageIndex: 0,
      variants: [{ size: "Pack", price: pack.price }]
    };

    addToCart(fakeProduct, "Pack");
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCFAED] flex items-center justify-center">
        <FaSpinner className="animate-spin text-[var(--secondary-color)]" size={48} />
      </div>
    );
  }

  if (error || !pack) {
    return (
      <div className="min-h-screen bg-[#FCFAED] flex flex-col items-center justify-center gap-4">
        <FaBoxOpen className="text-gray-400" size={64} />
        <p className="text-xl text-gray-500">{error}</p>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 px-6 py-3 bg-[var(--secondary-color)] text-white rounded-xl hover:bg-[var(--primary-color)] transition"
        >
          <FaArrowLeft /> Retour
        </button>
      </div>
    );
  }

  // Combine images to display
  let displayImages = [];
  if (pack.images && pack.images.length > 0) {
    displayImages = [...pack.images];
  } else if (pack.bannerImage) {
    displayImages = [pack.bannerImage];
  }

  // Original price calculation
  const originalTotalPrice = pack.products?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0;

  const jsonLd = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": pack.name,
    "image": displayImages.map(img => getImageUrl(img)),
    "description": pack.description || `Pack spécial ${pack.name}`,
    "offers": {
      "@type": "Offer",
      "priceCurrency": "TND",
      "price": pack.price,
      "availability": "https://schema.org/InStock"
    }
  };

  return (
    <div className="min-h-[calc(100vh)] pt-24 bg-[#FCFAED] py-8 flex flex-col items-center justify-center relative">
      <SEO 
        title={`${pack.name} | Offre Spéciale`} 
        description={pack.description?.substring(0, 300) || `Découvrez notre offre spéciale : ${pack.name}.`} 
        image={displayImages[0] ? getImageUrl(displayImages[0]) : null}
        type="product"
        jsonLd={jsonLd}
      />
      <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 flex flex-col">

        {/* Bouton retour */}
        <div className="mb-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-[var(--primary-color)] hover:text-[var(--secondary-color)] font-bold transition lg:bg-white lg:px-5 lg:py-2 lg:rounded-full lg:shadow-sm hover:shadow-md hover:scale-105 text-sm"
          >
            <FaArrowLeft size={12} /> Retour
          </button>
        </div>

        {/* Card Container */}
        <div className="bg-white rounded-[1.5rem] shadow-xl border border-amber-100/50 flex flex-col lg:flex-row mb-8 overflow-hidden">

          {/* ── Colonne images ──────────────────────────── */}
          <div className="lg:w-1/2 bg-[#FDFBF4] p-6 flex flex-col items-center justify-center border-b lg:border-b-0 lg:border-r border-amber-100">

            <div className="relative rounded-2xl overflow-hidden bg-white shadow-sm border border-amber-100 w-full max-w-sm aspect-square mb-4 group">
              {displayImages.length > 0 ? (
                <img
                  src={getImageUrl(displayImages[activeImage])}
                  alt={pack.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              ) : (
                <div className="flex items-center justify-center h-full bg-gradient-to-tr from-amber-50 to-orange-50">
                  <FaPercentage className="text-amber-200" size={80} />
                </div>
              )}
              {/* Badge type */}
              <span className="absolute top-4 left-4 px-4 py-1.5 text-xs font-black rounded-full shadow-md uppercase tracking-wider backdrop-blur-md bg-red-500 text-white border border-red-600 animate-pulse">
                -{pack.discountPercentage}%
              </span>
            </div>

            {/* Miniatures */}
            {displayImages.length > 1 && (
              <div className="flex gap-4 justify-center flex-wrap">
                {displayImages.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImage(i)}
                    className={`relative w-20 h-20 rounded-2xl overflow-hidden border-[3px] transition-all duration-300 ${activeImage === i
                      ? "border-[var(--secondary-color)] scale-110 shadow-lg hover:shadow-xl"
                      : "border-transparent hover:border-gray-300 opacity-60 hover:opacity-100"
                      }`}
                  >
                    <img
                      src={getImageUrl(img)}
                      alt={`vue ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Colonne infos ───────────────────────────── */}
          <div className="lg:w-1/2 p-6 lg:p-8 flex flex-col bg-white">

            <div className="space-y-6">
              {/* Nom & Ligne */}
              <div>
                <h1 className="text-3xl font-black text-gray-800 tracking-tight mb-1" style={{ fontFamily: "'Georgia', serif" }}>
                  {pack.name}
                </h1>
                <div className="h-1 w-16 rounded-full bg-[var(--secondary-color)]" />
              </div>

              {/* Description */}
              {pack.description && (
                <div>
                  <h2 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-1">
                    À propos de ce pack
                  </h2>
                  <p className="text-gray-600 leading-relaxed text-sm">
                    {pack.description}
                  </p>
                </div>
              )}

              {/* Contenu du pack */}
              <div className="bg-amber-50/40 rounded-2xl p-4 border border-amber-100/60">
                <h2 className="text-xs font-bold text-[var(--primary-color)] uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <FaBoxOpen /> Produits Inclus :
                </h2>
                <div className="space-y-2">
                  {pack.products?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center bg-white p-2.5 rounded-xl border border-amber-50 shadow-sm text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-700">{item.quantity}×</span>
                        <span className="font-medium text-gray-800">{item.productName} <span className="text-gray-500">({item.size})</span></span>
                      </div>
                      <span className="text-xs text-gray-400 line-through">{(item.price * item.quantity).toFixed(3)} DT</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Prix & Action */}
              <div className="pt-4 border-t border-gray-100 mt-auto">
                <div className="flex flex-col mb-4">
                  <div className="flex justify-between items-end mb-1">
                    <span className="text-gray-400 text-xs font-semibold line-through">Valeur réelle : {originalTotalPrice.toFixed(3)} DT</span>
                    <span className="bg-red-100 text-red-600 font-bold text-xs px-2 py-0.5 rounded">Économie: {pack.discountPercentage}%</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-gray-500 text-sm font-bold uppercase">Prix Pack :</span>
                    <span className="text-4xl font-black text-[var(--secondary-color)]">
                      {Number(pack.price).toFixed(3)} DT
                    </span>
                  </div>
                </div>

                <button
                  onClick={handleAddToCart}
                  disabled={added}
                  className={`w-full flex items-center justify-center gap-2 px-6 py-4 text-white text-lg font-black rounded-xl transition-all duration-300 transform ${added
                    ? "bg-green-500 scale-95 shadow-[0_4px_15px_-5px_rgba(34,197,94,0.4)]"
                    : "bg-gradient-to-r from-[var(--secondary-color)] to-[var(--primary-color)] hover:-translate-y-0.5 shadow-xl hover:shadow-2xl"
                    }`}
                >
                  {added ? (
                    <span className="flex items-center gap-2">
                      <FaCheck size={20} />
                      Ajouté au panier !
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <FaShoppingCart size={20} className="animate-bounce" />
                      Ajouter ce Pack au panier
                    </span>
                  )}
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PackDetailPage;
